import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useUserProfile } from "@vega-api/extended/auth/hooks";
import { useGetLimitedPartnerUsersPermissionView } from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import { getGetDealShortlistQueryKey } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import {
  getGetDealTeaserEmailByIdV2QueryKey,
  useGetDealTeaserEmailByIdV2,
} from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { type TeaserEmail } from "@vega-api/generated/types/teaserEmail";
import { type TeaserEmailContact } from "@vega-api/generated/types/teaserEmailContact";
import { TeaserEmailStatus } from "@vega-api/generated/types/teaserEmailStatus";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { m } from "@vega-core/paraglide/messages";

import { useDealRoles } from "../../hooks/useDealRoles";

import { DraftEmailForm } from "./DraftEmailForm";
import { draftEmailPresetQueryOptions } from "./draftEmailQueries";
import { teaserEmailToFormValues, userRecipientIds } from "./draftEmailUtils";

type DraftEmailModalProps = {
  dealId: string;
  prospect: DealShortlistItemResponse;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function DraftEmailModal({
  dealId,
  prospect,
  isOpen,
  onClose,
  onSuccess,
}: DraftEmailModalProps) {
  // A DRAFT or SCHEDULED email is reopened for editing/rescheduling. A SENT (or
  // CANCELLED) email is not reopened — clicking through routes to the
  // preset/create path so the GP composes a brand-new email to the same investor.
  const teaserEmail = prospect.teaserEmail;
  const editableDraftId =
    teaserEmail?.status === TeaserEmailStatus.DRAFT ||
    teaserEmail?.status === TeaserEmailStatus.SCHEDULED
      ? teaserEmail.emailId
      : undefined;
  const wasSent = teaserEmail?.status === TeaserEmailStatus.SENT;

  const queryClient = useQueryClient();
  const profileQuery = useUserProfile();
  const currentUser = profileQuery.data;
  const {
    isPending: isRolesPending,
    isError: isRolesError,
    isFetching: isRolesFetching,
    refetch: refetchRoles,
  } = useDealRoles();

  const accessUsersQuery = useGetLimitedPartnerUsersPermissionView(
    dealId,
    prospect.limitedPartner.id,
    {
      query: {
        enabled: isOpen,
        select: (response) => response.data?.users ?? [],
      },
    },
  );

  // The preset is only needed for a brand-new email once the modal is open and
  // the sender (the current user) is known; otherwise leave it disabled. See
  // the query factory for why this POST is modelled as a query.
  const teaserEmailSender: TeaserEmailContact | undefined =
    isOpen && !editableDraftId && currentUser
      ? {
          userId: currentUser.id,
          fullName: {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
          },
          email: currentUser.email,
        }
      : undefined;

  const presetQuery = useQuery(
    draftEmailPresetQueryOptions({
      dealId,
      dealShortlistItemId: prospect.id,
      sender: teaserEmailSender,
    }),
  );

  const draftQuery = useGetDealTeaserEmailByIdV2(
    dealId,
    editableDraftId ?? "",
    {
      query: {
        enabled: isOpen && !!editableDraftId,
        select: (response) => response.data,
      },
    },
  );

  const preset = presetQuery.data;
  const accessUsers = accessUsersQuery.data ?? [];
  const source = editableDraftId ? draftQuery.data : preset;
  const formSender = editableDraftId ? undefined : preset?.sender;
  // Lock state follows the fetched email, not the (snapshotted) row — so after
  // a refetch the modal flips from locked to editable in place.
  const isScheduled = draftQuery.data?.status === TeaserEmailStatus.SCHEDULED;

  const handleScheduleCancelled = (email: TeaserEmail) => {
    queryClient.setQueryData(
      getGetDealTeaserEmailByIdV2QueryKey(dealId, editableDraftId ?? ""),
      { data: email },
    );
    queryClient.invalidateQueries({
      queryKey: getGetDealShortlistQueryKey(dealId),
    });
  };

  const isLoading =
    accessUsersQuery.isPending ||
    isRolesPending ||
    (editableDraftId
      ? draftQuery.isPending
      : profileQuery.isPending || presetQuery.isPending);

  const isReady = !isLoading && !!source && (!!editableDraftId || !!formSender);

  const initialValues = teaserEmailToFormValues(source);

  const renderContent = () => {
    if (isRolesError) {
      return (
        <Notice
          action={
            <ThemedButton
              dataTestId="draft-email-roles-retry"
              isLoading={isRolesFetching}
              onClick={() => refetchRoles()}
              type="button"
              variant="text"
            >
              {m.btn_tryAgain()}
            </ThemedButton>
          }
          content={m.coInvestments_draftEmail_rolesError()}
          dataTestId="draft-email-roles-error"
          type={NoticeType.ERROR}
        />
      );
    }

    if (isReady) {
      return (
        <>
          {wasSent ? (
            <Notice
              content={m.coInvestments_draftEmail_alreadySent()}
              dataTestId="draft-email-already-sent"
              type={NoticeType.INFO}
            />
          ) : null}
          {isScheduled ? (
            <Notice
              content={m.coInvestments_draftEmail_scheduledLockNotice()}
              dataTestId="draft-email-scheduled-lock"
              type={NoticeType.INFO}
            />
          ) : null}
          <DraftEmailForm
            accessUsers={accessUsers}
            dealId={dealId}
            dealShortlistItemId={prospect.id}
            emailId={editableDraftId}
            initialValues={initialValues}
            isScheduled={isScheduled}
            limitedPartnerId={prospect.limitedPartner.id}
            onCancel={onClose}
            onSaved={onSuccess}
            onScheduleCancelled={handleScheduleCancelled}
            seedUserIds={userRecipientIds(source)}
            sender={formSender}
          />
        </>
      );
    }

    return (
      <div className="flex min-h-40 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  };

  return (
    <ActionaryModal
      dataTestId="draft-email-modal"
      description={m.coInvestments_draftEmail_description()}
      fluidHeight
      open={isOpen}
      setOpen={(open) => {
        if (!open) onClose();
      }}
      title={m.coInvestments_draftEmail_title()}
    >
      {renderContent()}
    </ActionaryModal>
  );
}
